/**
 * Authority Assigned Complaints View
 * Dedicated route component for Authority assigned complaints management
 */

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AssignedComplaintsView } from './index';
import { AuthorityEditComplaintDialog } from '../../components/dialogs';
import { useComplaints, useSettings } from '../../hooks/useRedux';
import { 
  clearAuthorityAssignedComplaints,
  clearCurrentComplaint,
  fetchAuthorityAssignedComplaints,
  setCurrentComplaint,
} from '../../store/slices/complaintSlice';
import complaintService from '../../services/complaintService';
import noteService from '../../services/noteService';
import Toast from '../../components/Toast';
import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/tableExport';

const AuthorityAssignedComplaintsView = () => {
  const {
    assignedComplaints,
    currentComplaint,
    dispatch,
  } = useComplaints();

  const {
    complaintTypes,
    complaintStatuses,
    complaintPriorities,
    departments,
  } = useSettings();

  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef(null);
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success',
  });
  const hasFetchedRef = useRef(false);

  // Helper function to extract names from objects (for backward compatibility)
  const getOptionValue = (item) => {
    return typeof item === 'object' ? item.name : item;
  };

  // Helper function to get ID from name (for complaint types)
  // Returns the ID if found, otherwise returns null
  const getComplaintTypeId = (name) => {
    if (!name || !complaintTypes || complaintTypes.length === 0) return null;
    
    const normalizedName = String(name).trim().toLowerCase();
    const type = complaintTypes.find(t => {
      if (!t) return false;
      const typeName = typeof t === 'object' && t !== null ? (t.name || t.label || '') : String(t);
      return String(typeName).trim().toLowerCase() === normalizedName;
    });
    
    // Return ID if type is an object with id property
    if (type && typeof type === 'object' && type !== null && type.id !== undefined) {
      return type.id;
    }
    
    return null;
  };

  // Convert Redux state to arrays of names for dropdowns
  const complaintTypesList = useMemo(() => 
    complaintTypes.map(getOptionValue), 
    [complaintTypes]
  );
  
  const complaintStatusesList = useMemo(() => 
    complaintStatuses.map(getOptionValue), 
    [complaintStatuses]
  );
  
  const prioritiesList = useMemo(() => 
    complaintPriorities.map(getOptionValue), 
    [complaintPriorities]
  );
  
  const departmentsList = useMemo(() => 
    departments.map(getOptionValue), 
    [departments]
  );

  useEffect(() => {
    // Only fetch if we haven't fetched yet, not currently loading, and no error
    if (
      !hasFetchedRef.current &&
      !assignedComplaints.isLoading &&
      !assignedComplaints.error
    ) {
      hasFetchedRef.current = true;
      dispatch(fetchAuthorityAssignedComplaints());
    }
  }, [
    assignedComplaints.isLoading,
    assignedComplaints.error,
    dispatch,
  ]);

  useEffect(
    () => () => {
      dispatch(clearAuthorityAssignedComplaints());
      hasFetchedRef.current = false;
    },
    [dispatch]
  );

  const complaints = useMemo(
    () => assignedComplaints.data || [],
    [assignedComplaints.data]
  );

  // Handler functions
  const handleComplaintEdit = (complaint) => {
    if (!complaint) return;
    dispatch(setCurrentComplaint(complaint));
    setSelectedComplaintId(complaint.id || complaint.complaint_id);
  };

  const handleSaveComplaint = async (formData) => {
    if (!currentComplaint || !selectedComplaintId) return;

    setLoading(true);
    try {
      // Get complaint type ID from name
      const complaintTypeName = formData.assign_complain_type || '';
      const complaintTypeId = getComplaintTypeId(complaintTypeName);
      
      // Validate that we have a complaint type ID
      if (!complaintTypeId && complaintTypeName) {
        setToast({
          isVisible: true,
          message: 'Could not find complaint type ID. Please try again.',
          type: 'error',
        });
        setLoading(false);
        return;
      }
      
      // Map form data to API payload format
      // complaint_type should be the ID, not the name
      const payload = {
        complaint_type: complaintTypeId || '',
        template_type: formData.selected_template || '',
        remark: formData.remark || '',
      };

      const result = await complaintService.updateAuthorityComplaint(selectedComplaintId, payload);

      if (result.success) {
        setToast({
          isVisible: true,
          message: result.message || 'Complaint updated successfully',
          type: 'success',
        });
        
        // Refresh the complaints list
        dispatch(fetchAuthorityAssignedComplaints());
        
        // Close dialog
        setSelectedComplaintId(null);
        dispatch(clearCurrentComplaint());
      } else {
        setToast({
          isVisible: true,
          message: result.message || 'Failed to update complaint',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isVisible: true,
        message: 'An error occurred while updating the complaint',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (attachments = [], selectedRecipients = []) => {
    if (!newNote.trim() && attachments.length === 0) return;
    if (!selectedComplaintId || !currentComplaint) return;

    setLoading(true);
    try {
      // Use complaint_id (string like "TYO966075714") for the API
      const complaintId = currentComplaint.complaint_id || selectedComplaintId;
      
      // Process recipients: extract IDs from selectedRecipients
      const sendTo = [];
      if (Array.isArray(selectedRecipients) && selectedRecipients.length > 0) {
        selectedRecipients.forEach((recipient) => {
          if (typeof recipient === 'string') {
            // Check if it's an officer (format: "officer-{id}")
            if (recipient.startsWith('officer-')) {
              const officerId = recipient.replace('officer-', '');
              if (officerId) {
                sendTo.push(officerId);
              }
            } 
            // Check if it's the complainant
            else if (recipient === 'complainant') {
              // Get complainer ID from complaint data
              const complainerId = currentComplaint.user?.id || currentComplaint.user_id;
              if (complainerId) {
                sendTo.push(complainerId);
              }
            }
          } else if (typeof recipient === 'object' && recipient.id) {
            // Handle object format
            const recipientId = recipient.id;
            if (recipientId.startsWith('officer-')) {
              const officerId = recipientId.replace('officer-', '');
              if (officerId) {
                sendTo.push(officerId);
              }
            } else if (recipientId === 'complainant') {
              const complainerId = currentComplaint.user?.id || currentComplaint.user_id;
              if (complainerId) {
                sendTo.push(complainerId);
              }
            }
          }
        });
      }

      const noteData = {
        message: newNote.trim(),
        sendTo: sendTo,
        files: attachments || [],
      };

      const result = await noteService.sendAuthorityInternalNote(complaintId, noteData);
      
      if (result.success) {
        setToast({
          isVisible: true,
          message: result.message || result.data?.message || 'Internal note sent successfully',
          type: 'success',
        });
        setNewNote('');
        // Optionally refresh complaint data to show new note in history
        dispatch(fetchAuthorityAssignedComplaints());
      } else {
        setToast({
          isVisible: true,
          message: result.message || result.data?.message || 'Failed to send internal note',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isVisible: true,
        message: error.message || 'An error occurred while sending the note',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setSelectedComplaintId(null);
    dispatch(clearCurrentComplaint());
  };

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get columns for export
  const getColumns = () => [
    { field: 'complaint_id', headerName: 'Complaint ID' },
    { field: 'complainant_id', headerName: 'Complainant ID' },
    { field: 'complainant_name', headerName: 'Complainant Name' },
    { field: 'complaint_type', headerName: 'Complaint Type' },
    { field: 'department_name', headerName: 'Department' },
    { field: 'priority', headerName: 'Priority' },
    { field: 'complaint_status', headerName: 'Status' },
    { field: 'remark', headerName: 'Remark' },
    { field: 'documents', headerName: 'Documents' },
  ];

  // Helper function to extract document names from files array
  const getDocumentNames = (complaint) => {
    const documents = [];
    
    // Check for files array
    if (Array.isArray(complaint.files) && complaint.files.length > 0) {
      complaint.files.forEach((file) => {
        if (file) {
          const filePath = typeof file === 'string' ? file : file.file_path;
          if (filePath) {
            const fileName = filePath.split('/').pop() || filePath;
            documents.push(fileName);
          }
        }
      });
    }
    
    // Check for documents array (fallback)
    if (documents.length === 0 && Array.isArray(complaint.documents) && complaint.documents.length > 0) {
      complaint.documents.forEach((doc) => {
        if (doc) {
          const filePath = typeof doc === 'string' ? doc : doc.file_path;
          if (filePath) {
            const fileName = filePath.split('/').pop() || filePath;
            documents.push(fileName);
          }
        }
      });
    }
    
    // Check for supporting_documents and general_documents
    if (documents.length === 0) {
      const allDocs = [
        ...(Array.isArray(complaint.supporting_documents) ? complaint.supporting_documents : []),
        ...(Array.isArray(complaint.general_documents) ? complaint.general_documents : [])
      ];
      
      allDocs.forEach((doc) => {
        if (doc) {
          const filePath = typeof doc === 'string' ? doc : doc.file_path;
          if (filePath) {
            const fileName = filePath.split('/').pop() || filePath;
            documents.push(fileName);
          }
        }
      });
    }
    
    return documents.join(', ');
  };

  // Get filtered data for export with complainant fields mapped
  const getFilteredData = () => {
    const data = complaints.map(complaint => ({
      ...complaint,
      complainant_id: complaint.user?.id || complaint.user_id || '',
      complainant_name: complaint.name || complaint.complainant || complaint.user?.name || '',
      documents: getDocumentNames(complaint),
    }));

    if (!searchTerm.trim()) return data;
    
    const term = searchTerm.trim().toLowerCase();
    return data.filter(complaint => {
      const complaintId = String(complaint.complaint_id || '').toLowerCase();
      const complainantId = String(complaint.complainant_id || '').toLowerCase();
      const complainantName = String(complaint.complainant_name || '').toLowerCase();
      const complaintType = String(complaint.complaint_type || '').toLowerCase();
      const department = String(complaint.department_name || '').toLowerCase();
      const priority = String(complaint.priority || '').toLowerCase();
      const status = String(complaint.complaint_status || complaint.status || '').toLowerCase();
      const remark = String(complaint.remark || '').toLowerCase();

      return complaintId.includes(term) ||
             complainantId.includes(term) ||
             complainantName.includes(term) ||
             complaintType.includes(term) ||
             department.includes(term) ||
             priority.includes(term) ||
             status.includes(term) ||
             remark.includes(term);
    });
  };

  const handleExportCSV = () => {
    try {
      const filteredData = getFilteredData();
      exportToCSV(filteredData, getColumns(), 'assigned-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredData();
      exportToExcel(filteredData, getColumns(), 'assigned-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredData();
      exportToPDF(filteredData, getColumns(), 'assigned-complaints');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header with Search (Left) and Buttons (Right) */}
      <div className="flex justify-between items-center pb-4 flex-shrink-0 gap-4">
        {/* Search Bar - Left Aligned */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-96">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-gray-700"
              placeholder="Search by complaint ID, type, department, priority, status, or remark..."
            />
          </div>
        </div>

        {/* Buttons - Right Aligned */}
        <div className="flex items-center gap-3">
          {/* Download Button with Dropdown */}
          <div className="relative" ref={downloadDropdownRef}>
            <button
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
              <svg className={`w-4 h-4 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Download Dropdown Menu */}
            {showDownloadDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download CSV</span>
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download Excel</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <AssignedComplaintsView
          complaints={complaints}
          searchTerm={searchTerm}
          filterStatus="all"
          onComplaintEdit={handleComplaintEdit}
          isLoading={assignedComplaints.isLoading}
          error={assignedComplaints.error}
        />
      </div>

      {/* Complaint Edit Modal - Authority View */}
      <AuthorityEditComplaintDialog
        isOpen={!!currentComplaint && !!selectedComplaintId}
        complaint={currentComplaint}
        complaintStatuses={complaintStatusesList}
        complaintTypes={complaintTypesList}
        departments={departmentsList}
        officers={[]}
        priorities={prioritiesList}
        complaintLogs={[]}
        logsLoading={false}
        newNote={newNote}
        onNoteChange={setNewNote}
        onAddNote={handleAddNote}
        onSave={handleSaveComplaint}
        onClose={handleDialogClose}
        showHistory={true}
        loading={loading}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default AuthorityAssignedComplaintsView;
